import { WSList, WSText } from "@wingspanhq/fe-component-library";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LoadingPlaceholder } from "../../../components/LoadingPlaceholder";
import { getCustomerEntity } from "../../../services/api/onboarding/customer";
import { getVerificationMissingData } from "../../../services/api/onboarding/missingFields";
import { getVerifications } from "../../../services/api/onboarding/verifications";
import {
  isUsProfileAction,
  selectTaxVerificationAction,
  TaxVerificationAction
} from "../../../shared/services/selectTaxVerificationAction";
import { EmbeddedError } from "../components/EmbeddedError";
import { EmbeddedLayout } from "../components/EmbeddedLayout";
import { TaxCorrectionForm } from "../components/TaxCorrectionForm";
import { TaxCorrectionDocuments } from "../components/TaxCorrectionDocuments";
import { sendMessageToParent } from "../utils/postMessage";

type State = {
  entity: Awaited<ReturnType<typeof getCustomerEntity>>;
  action: TaxVerificationAction;
};

export const RouteCompleteVerification: React.FC = () => {
  const location = useLocation();
  const [level] = useState(() =>
    new URLSearchParams(location.search).get("level")
  );
  const [state, setState] = useState<State>();
  const [error, setError] = useState<string>();
  const [completed, setCompleted] = useState(false);
  const active = useRef(true);
  const sentComplete = useRef(false);

  useEffect(() => {
    active.current = true;
    if (level !== "tax") {
      setError("Only tax verification is supported.");
      return;
    }
    (async () => {
      const verifications = await getVerifications();
      if (verifications.tax !== "UpdateRequired") {
        throw new Error("Tax verification does not require a correction.");
      }
      const [entity, missingData] = await Promise.all([
        getCustomerEntity(),
        getVerificationMissingData("Tax")
      ]);
      if (active.current)
        setState({ entity, action: selectTaxVerificationAction(missingData) });
    })().catch(() => {
      if (active.current)
        setError(
          "Unable to open tax verification. Refresh the verification status and try again."
        );
    });
    return () => {
      active.current = false;
    };
  }, [level]);

  // Submission is not approval. Re-read missing data to catch an excluded next
  // action before notifying the consumer. Never infer completion on initial load.
  const onSubmitted = async (documents = false) => {
    const action = selectTaxVerificationAction(
      await getVerificationMissingData("Tax")
    );
    if (!active.current || !state || sentComplete.current) return;
    const hasNextCorrection =
      action.type === "fields" ||
      (action.type === "documents" &&
        (!documents ||
          state.action.type !== "documents" ||
          action.token !== state.action.token));
    if (isUsProfileAction(action) || hasNextCorrection) {
      setState({ ...state, action });
      return;
    }
    // Footprint's top-level token can remain present until its webhook arrives.
    sentComplete.current = true;
    setCompleted(true);
    sendMessageToParent("complete", { level: "tax" });
  };

  let content;
  if (error) content = <EmbeddedError message={error} />;
  else if (completed)
    content = (
      <WSText.ParagraphSm>
        Your information has been submitted.
      </WSText.ParagraphSm>
    );
  else if (!state) content = <LoadingPlaceholder blank />;
  else if (isUsProfileAction(state.action))
    content = (
      <WSList gap="L">
        <WSText.Heading4>
          This verification flow is not supported
        </WSText.Heading4>
        <WSText.ParagraphSm>
          Additional U.S. person information or documents are required. This
          step cannot be completed here. Return to your platform for assistance.
        </WSText.ParagraphSm>
      </WSList>
    );
  else if (state.action.type === "fields")
    content = (
      <TaxCorrectionForm
        entity={state.entity}
        requiredFields={state.action.requiredFields}
        onSubmitted={onSubmitted}
      />
    );
  else if (state.action.type === "documents")
    content = (
      <TaxCorrectionDocuments
        key={state.action.token}
        token={state.action.token}
        onSubmitted={() => onSubmitted(true)}
      />
    );
  else
    content = (
      <EmbeddedError message="This verification has no supported correction to complete." />
    );

  return <EmbeddedLayout>{content}</EmbeddedLayout>;
};
